import { useState } from "react";
import "./PortfolioMap.scss";
import { Treemap, ResponsiveContainer, Tooltip } from "recharts";
import { generateColors } from "../../utils/genericFunctions";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import newRequest from "../../utils/newRequest";
import numeral from "numeral";
import { useSelector } from "react-redux";
import { useSocket } from "../../redux/useSocket";

const PortfolioMap = () => {
    const socket = useSocket();
    const { t } = useTranslation();
    const { currentUser } = useSelector((state) => state.user);
    const options = [
        { _id: "premium", title: "premium" },
        { _id: "commission", title: "commission" },
    ];
    const [portfolio, setPortfolio] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [uwStats, setUwStats] = useState(null);
    const [selectedMenu, setSelectedMenu] = useState(options[0]);
    const [menus, setMenus] = useState(options);

    // Color palette
    let colors = 0;

    const fetchStats = async () => {
        try {
            const res = await newRequest.get("/stats/getStats");
            const rawData = res.data?.combinedGroups?.uwGroupByProduct || [];
            setPortfolio(rawData);

            colors = generateColors(rawData.length);

            // Compute total based on selected field
            const total = rawData.reduce((sum, item) => {
                return sum + (selectedMenu?._id === "premium" ? item.policy_lc_premium : item.commission_lc_amount);
            }, 0);

            const data = rawData.map((item, index) => {
                const value = selectedMenu?._id === "premium" ? item.policy_lc_premium : item.commission_lc_amount;
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                return {
                    name: item.product_name_xx,
                    code: item.pl_product_code,
                    premium: item.policy_lc_premium,
                    commission: item.commission_lc_amount,
                    size: value,
                    fill: colors[index],
                    percentage: `${percentage}%`
                };
            });

            setChartData(data);
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        if (!socket) return;

        const handler = async () => {
            console.log("got a signal to refresh...");
            await fetchStats();
        };

        socket.on("refreshStats", handler);

        return () => {
            socket.off("refreshStats", handler);
        };
    }, [socket]);

    useEffect(() => {
        fetchStats();
    }, [selectedMenu]);

    // Custom block render
    const CustomizedContent = ({
        depth, x, y, width, height, index, name, code, premium, commission
    }) => {
        const isHovered = index === CustomizedContent.hoveredIndex;
        // Use the fill color from the main data array
        const baseColor = chartData[index]?.fill;
        const fill = isHovered ? "#000" : baseColor; // Example hover effect
        const textColor = isHovered ? "#fff" : "#000";

        // Don't render anything for tiny rectangles
        if (width < 10 || height < 10) {
            return null;
        }

        return (
            <g
                onMouseEnter={() => CustomizedContent.setHoveredIndex(index)}
                onMouseLeave={() => CustomizedContent.setHoveredIndex(null)}
                style={{ cursor: "pointer" }}
            >
                {/* The main rounded rectangle. It has its own fill and stroke. */}

                <rect
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    rx={8}
                    ry={8}
                    style={{
                        fill,
                        stroke: "#fff",
                        strokeWidth: 2,
                        strokeLinejoin: "round",
                        strokeLinecap: "round",
                        transition: "fill 0.2s ease",
                    }}
                />

                {/* 
                    This clipping path ensures the text inside doesn't spill out
                    of the rounded corners, which is good practice.
                */}
                <defs>
                    <clipPath id={`clip-${index}`}>
                        <rect x={x} y={y} width={width} height={height} rx={8} ry={8} />
                    </clipPath>
                </defs>
                <g clipPath={`url(#clip-${index})`}>
                    {width > 100 && height > 60 && (
                        <>
                            {(() => {
                                const yStart = y + 20;
                                const lineHeight = 16;
                                let i = 0;

                                return (
                                    <>
                                        <text x={x + 8} y={yStart + lineHeight * i++} fontSize={12} fontWeight="bold" fill={textColor}>
                                            {name}
                                        </text>
                                        <text x={x + 8} y={yStart + lineHeight * i++} fontSize={10} fill={textColor}>
                                            {t('code')}: {code}
                                        </text>
                                        <text x={x + 8} y={yStart + lineHeight * i++} fontSize={10} fill={textColor}>
                                            {selectedMenu._id === "premium"
                                                ? `${t('premium')}: ${`${currentUser?.currency_code_xx} ${numeral(premium).format("0,0.00")}`}`
                                                : `${t('commission')}: ${`${currentUser?.currency_code_xx} ${numeral(commission).format("0,0.00")}`}`}
                                        </text>
                                        <text x={x + 8} y={yStart + lineHeight * i++} />
                                        <text x={x + 8} y={yStart + lineHeight * i++} fontSize={16} fontWeight="bold" fill={textColor}>
                                            {chartData[index]?.percentage}
                                        </text>
                                    </>
                                );
                            })()}
                        </>

                    )}
                </g>
            </g>
        );
    };

    const [hoveredIndex, setHoveredIndex] = useState(null);

    // Pass hover index state to static method
    CustomizedContent.hoveredIndex = hoveredIndex;
    CustomizedContent.setHoveredIndex = setHoveredIndex;

    // Tooltip formatter
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length > 0 && payload[0]?.payload) {
            const { name, code, premium, commission, percentage } = payload[0].payload;
            return (
                <div style={{
                    background: "rgba(255, 255, 255, 0.9)",
                    border: "1px solid #ccc",
                    padding: "10px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    gap: "5px",
                    boxShadow: "2px 2px 5px rgba(0,0,0,0.1)"
                }}>
                    <strong>{name}</strong><br />
                    {t('code')}: {code}<br /><br />
                    {t('premium')}: {`${currentUser?.currency_code_xx} ${numeral(premium).format("0,0.00")}`}<br />
                    {t('commission')}: {`${currentUser?.currency_code_xx} ${numeral(commission).format("0,0.00")}`}
                    <br /><br />
                    {t('percentage')}: {percentage}
                </div>
            );
        }
        return null;
    };

    return (
        <div className='portfolio-map' style={{ width: '100%', height: chartData?.length > 0 ? 500 : 100 }}>
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", marginBottom: "5px", justifyContent: "space-between", gap: "5px", padding: "0px 5px" }}>
                <span className="sub-title">{`${t('portfolio').toUpperCase()} ${t('using').toUpperCase()} ${selectedMenu?.title?.toUpperCase()}`}</span>
                <div className="menus-wrapper">
                    {menus?.map((row, index) => {
                        return (
                            <div key={index}
                                className={`${selectedMenu?._id === row?._id
                                    ? "single-menu active"
                                    : "single-menu"
                                    }`}
                                onClick={() => setSelectedMenu(row)}>
                                <span className="sub-title xrsmall">{t(row?.title).toUpperCase()}</span>
                            </div>
                        )
                    })}
                </div>
            </div>
            {chartData?.length > 0 ? <ResponsiveContainer>
                <Treemap
                    data={chartData}
                    dataKey="premium"
                    aspectRatio={4 / 3}
                    stroke="none"
                    fill="#8884d8"
                    content={<CustomizedContent />}
                    isAnimationActive={false}
                >
                    <Tooltip content={<CustomTooltip />} />
                </Treemap>
            </ResponsiveContainer>
                :
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50px" }}>
                    <span className="main-title">{t('noDataFound')}</span>
                </div>}
        </div>
    )
}

export default PortfolioMap;